import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  ArrowRight,
  Lock,
  ShieldCheck,
  Database,
  LineChart,
  FileSpreadsheet,
  Sparkles,
  Cpu,
  Layers,
  Workflow,
  Brain,
  Building2,
  Globe2,
  CheckCircle2,
  XCircle,
  Download,
} from 'lucide-react';

// =================================================================
// META SWAP
// =================================================================
const SIAM_META = {
  title: 'SIAM × TMG · A live industry intelligence layer for SIAM',
  description:
    'A concept blueprint for SIAM, the Society of Indian Automobile Manufacturers — modernise OEM data ingestion, build a live analytics platform, multiply member value, and become the data backbone of Indian automotive.',
  image: 'https://themobilitygroup.co/og-image.png?v=2',
  url: 'https://themobilitygroup.co/projects/SIAM',
};

const useSIAMMeta = () => {
  useEffect(() => {
    const originalTitle = document.title;
    const originals = {};
    const setMeta = (sel, v) => {
      const el = document.querySelector(sel);
      if (el) {
        originals[sel] = el.getAttribute('content');
        el.setAttribute('content', v);
      }
    };
    document.title = SIAM_META.title;
    setMeta('meta[property="og:title"]', SIAM_META.title);
    setMeta('meta[property="og:description"]', SIAM_META.description);
    setMeta('meta[property="og:url"]', SIAM_META.url);
    setMeta('meta[name="twitter:title"]', SIAM_META.title);
    setMeta('meta[name="twitter:description"]', SIAM_META.description);
    setMeta('meta[name="description"]', SIAM_META.description);
    return () => {
      document.title = originalTitle;
      Object.entries(originals).forEach(([sel, v]) => {
        const el = document.querySelector(sel);
        if (el && v !== null) el.setAttribute('content', v);
      });
    };
  }, []);
};

// =================================================================
// GATE
// =================================================================
const STORAGE_KEY = 'siam_unlock';
const PASSCODE = 'SIAM2026';

const Gate = ({ onUnlock }) => {
  const [val, setVal] = useState('');
  const [err, setErr] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (val.trim().toUpperCase() === PASSCODE) {
      sessionStorage.setItem(STORAGE_KEY, '1');
      onUnlock();
    } else {
      setErr('Passcode does not match.');
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900 relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.5] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      {/* Soft amber + navy glows */}
      <div
        className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #1e3a8a 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-32 -right-24 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)' }}
      />

      <div className="relative max-w-xl mx-auto px-6 md:px-12 pt-24 md:pt-40 pb-16">
        <div className="mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber mb-4">
            A concept presentation · for SIAM leadership
          </div>
          <div className="flex items-center gap-4 mb-6">
            <SIAMWordmark />
            <span className="font-display text-xl text-zinc-500">×</span>
            <span className="grid h-10 w-10 place-items-center border border-amber bg-amber/10">
              <span className="font-mono text-amber text-xs font-semibold">TMG</span>
            </span>
          </div>
        </div>

        <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl leading-[1.02] text-balance">
          A working session for{' '}
          <span className="text-amber">SIAM leadership.</span>
        </h1>
        <p className="mt-6 text-zinc-700 text-base md:text-lg leading-relaxed">
          A privileged preview of how SIAM&apos;s industry data could become a
          live intelligence product — strictly with the data SIAM already
          receives, no new collection, no OEM disintermediation.
        </p>

        <form onSubmit={submit} className="mt-12">
          <div className="overline text-zinc-500 mb-3 font-mono text-[10px] uppercase tracking-[0.22em]">
            Session passcode
          </div>
          <div className="flex">
            <input
              type="password"
              value={val}
              onChange={(e) => {
                setVal(e.target.value);
                setErr('');
              }}
              autoFocus
              autoComplete="off"
              data-testid="siam-passcode-input"
              placeholder="ENTER PASSCODE"
              className="flex-1 bg-zinc-50 border border-zinc-300 px-4 py-3 font-mono text-sm text-zinc-900 placeholder-zinc-400 focus:border-amber focus:outline-none focus:ring-0 tracking-[0.18em] uppercase"
            />
            <button
              type="submit"
              data-testid="siam-passcode-submit"
              className="bg-amber text-black px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-amber/90 inline-flex items-center gap-2"
            >
              Enter <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {err && (
            <div className="mt-4 inline-flex items-center gap-2 text-rose-400 font-mono text-xs uppercase tracking-[0.18em]">
              <Lock className="w-3 h-3" /> {err}
            </div>
          )}
        </form>

        <p className="mt-16 text-xs text-zinc-500 leading-relaxed max-w-md">
          Confidential preview. Numbers and architecture are illustrative and
          prepared for a closed discussion with SIAM. SIAM marks shown for
          context — not affiliated, not endorsed.
        </p>

        <div className="mt-10">
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-amber inline-flex items-center gap-2"
          >
            ← Back to themobilitygroup.co
          </Link>
        </div>
      </div>
    </div>
  );
};

// =================================================================
// VISUAL ATOMS
// =================================================================
const SIAMWordmark = ({ small = false }) => (
  <span
    className={`inline-flex items-baseline ${small ? 'text-base' : 'text-xl'}`}
  >
    <span
      className="font-display font-bold tracking-tight"
      style={{ color: '#3B82F6' }}
    >
      SIAM
    </span>
  </span>
);

const Kicker = ({ children, color = 'amber' }) => (
  <div
    className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-3 ${
      color === 'amber' ? 'text-amber' : color === 'siam' ? 'text-blue-400' : 'text-cyan-300'
    }`}
  >
    {children}
  </div>
);

const SectionHeader = ({ kicker, title, color = 'amber' }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <div
        className={`w-10 h-px ${
          color === 'amber' ? 'bg-amber' : color === 'siam' ? 'bg-blue-500' : 'bg-cyan-300'
        }`}
      />
      <span
        className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
          color === 'amber' ? 'text-amber' : color === 'siam' ? 'text-blue-400' : 'text-cyan-300'
        }`}
      >
        {kicker}
      </span>
    </div>
    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-balance text-zinc-900 leading-[1.05] max-w-5xl">
      {title}
    </h2>
  </div>
);

// =================================================================
// HERO
// =================================================================
const Hero = () => (
  <section className="relative px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-20 md:pb-28 overflow-hidden border-b border-zinc-200">
    {/* Layered glows */}
    <div
      className="absolute -top-20 left-1/4 w-[600px] h-[600px] rounded-full opacity-25 blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(circle, #1e3a8a 0%, transparent 70%)' }}
    />
    <div
      className="absolute top-32 right-0 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)' }}
    />
    <div
      className="absolute inset-0 opacity-[0.45] pointer-events-none"
      style={{
        backgroundImage:
          'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
      }}
    />

    <div className="relative">
      <Kicker>Concept presentation · SIAM leadership</Kicker>

      <div className="flex items-center gap-5 mb-10">
        <SIAMWordmark />
        <span className="font-display text-2xl text-zinc-500">×</span>
        <span className="grid h-10 w-10 place-items-center border border-amber bg-amber/10">
          <span className="font-mono text-amber text-xs font-semibold">TMG</span>
        </span>
      </div>

      <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[0.98] text-balance max-w-5xl">
        A live{' '}
        <span style={{ color: '#3B82F6' }}>industry intelligence</span>{' '}
        layer for <span className="text-amber">SIAM.</span>
      </h1>

      <p className="mt-8 max-w-3xl text-base md:text-lg text-zinc-700 leading-relaxed">
        SIAM already sits on the most valuable manufacturing dataset in Indian
        automotive — submitted by ~500 OEM members, every month, across two-,
        three-, four-wheelers and CVs. Today it moves on Excel
        sheets and PDFs. We believe it can move on dashboards, APIs and
        AI-generated reports — without changing what SIAM collects or who owns
        the data.
      </p>

      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 max-w-4xl border border-zinc-200">
        {[
          { v: '~500', l: 'OEM members' },
          { v: '~₹5 Cr', l: 'Annual reports revenue' },
          { v: 'Excel', l: 'Current data exchange' },
          { v: '21 days', l: 'Typical report turnaround' },
        ].map((s) => (
          <div key={s.l} className="bg-zinc-50 p-5 md:p-6">
            <div className="font-display text-3xl md:text-4xl text-amber tabular-nums">
              {s.v}
            </div>
            <div className="mt-2 text-xs md:text-sm text-zinc-600 leading-snug">
              {s.l}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-4">
        <a
          href="#vision"
          data-testid="siam-cta-vision"
          className="inline-flex items-center gap-2 bg-amber text-black px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber/90"
        >
          See the vision <ArrowUpRight className="w-4 h-4" />
        </a>
        <a
          href="/api/projects/siam/pdf"
          target="_blank"
          rel="noopener"
          data-testid="siam-cta-pdf"
          className="inline-flex items-center gap-2 border border-amber text-amber px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber hover:text-black"
        >
          <Download className="w-4 h-4" /> Download PDF
        </a>
        <a
          href="#why-tmg"
          data-testid="siam-cta-why-tmg"
          className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] hover:border-amber hover:text-amber"
        >
          Why TMG
        </a>
      </div>
    </div>
  </section>
);

// =================================================================
// THE STATUS QUO
// =================================================================
const PAIN = [
  {
    icon: FileSpreadsheet,
    t: 'Excel ping-pong',
    d:
      'OEM submission templates emailed back and forth · version conflicts · manual reconciliation by SIAM analysts.',
  },
  {
    icon: Workflow,
    t: '21-day cycle',
    d:
      'From submission deadline to a printable report — three weeks. By the time a CEO reads it, the market has moved.',
  },
  {
    icon: Layers,
    t: 'PDF-trapped intelligence',
    d:
      'Insights locked inside 80-page PDFs. No drill-down, no segment slicing, no time series, no API.',
  },
  {
    icon: ShieldCheck,
    t: 'Manual validation',
    d:
      'Inconsistent SKU naming, missing rows, unit mismatches — caught (or missed) by humans, not by rules.',
  },
  {
    icon: Database,
    t: 'No member self-service',
    d:
      'A member CEO who wants to slice "my segment share in Tier-2 cities, last 4 quarters" must email SIAM and wait.',
  },
  {
    icon: LineChart,
    t: 'Revenue ceiling',
    d:
      'Annual reports business plateaus at ~₹5 Cr because the product is one-size-fits-all paper. The data deserves more.',
  },
];

const StatusQuo = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200">
    <SectionHeader
      kicker="The status quo · honest audit"
      title={
        <>
          The data is gold.{' '}
          <span className="text-amber">The pipes are paper.</span>
        </>
      }
    />
    <p className="text-zinc-700 max-w-3xl mt-4 text-base leading-relaxed">
      SIAM&apos;s monthly cycle today — observed and inferred from member
      conversations:
    </p>

    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 mt-12 border border-zinc-200">
      {PAIN.map((p) => {
        const Icon = p.icon;
        return (
          <div key={p.t} className="bg-zinc-50 p-6 md:p-8">
            <div className="grid h-10 w-10 place-items-center border border-amber/40 bg-amber/10 mb-5">
              <Icon className="w-4 h-4 text-amber" />
            </div>
            <div className="font-display text-xl text-zinc-900">{p.t}</div>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{p.d}</p>
          </div>
        );
      })}
    </div>
  </section>
);

// =================================================================
// BEFORE / AFTER
// =================================================================
const BeforeAfter = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200 bg-zinc-50">
    <SectionHeader
      kicker="The shift"
      title={
        <>
          Same data. <span className="text-amber">Different product.</span>
        </>
      }
    />
    <p className="text-zinc-700 max-w-3xl mt-4">
      We&apos;re not asking SIAM to collect anything new. We&apos;re asking SIAM
      to <em className="text-amber not-italic">deliver</em> what it already
      collects, differently.
    </p>

    <div className="grid md:grid-cols-2 gap-px bg-zinc-200 mt-12 border border-zinc-200">
      {/* Before */}
      <div className="bg-zinc-50 p-6 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
          Today
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-zinc-900">
          The Excel cycle.
        </h3>
        <ul className="mt-6 space-y-3">
          {[
            'Monthly template emailed to 500 members',
            'Members return XLS at varying quality',
            'SIAM analysts merge, validate, format',
            'PDF report compiled and emailed back',
            'Members extract insights manually',
            'Custom asks → another round of email + Excel',
          ].map((l) => (
            <li key={l} className="flex items-start gap-3 text-sm text-zinc-600">
              <XCircle className="w-4 h-4 text-rose-400/80 mt-0.5 flex-shrink-0" />
              <span>{l}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 border-t border-zinc-200 pt-5 grid grid-cols-3 gap-3">
          <Stat n="21d" l="Cycle time" />
          <Stat n="₹5 Cr" l="Annual revenue" />
          <Stat n="0" l="APIs / dashboards" />
        </div>
      </div>

      {/* After */}
      <div className="bg-zinc-50 p-6 md:p-10 border-l-2 border-amber">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-2">
          With TMG · target state
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-zinc-900">
          The SIAM Intelligence Platform.
        </h3>
        <ul className="mt-6 space-y-3">
          {[
            'Members upload via secure portal or push via API',
            'Live validation flags issues on submit · no email tag',
            'Auto-merged, auto-anonymised industry dataset',
            'Members log in to a live dashboard for their segment',
            'AI-generated narrative reports — boardroom-ready in 2 min',
            'Premium tiers · custom views · benchmarking on demand',
          ].map((l) => (
            <li key={l} className="flex items-start gap-3 text-sm text-zinc-800">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <span>{l}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8 border-t border-zinc-200 pt-5 grid grid-cols-3 gap-3">
          <Stat n="<24h" l="Cycle time" tone="amber" />
          <Stat n="~₹25-50 Cr" l="3-yr revenue path" tone="amber" />
          <Stat n="Live" l="Member portal · APIs" tone="amber" />
        </div>
      </div>
    </div>
  </section>
);

const Stat = ({ n, l, tone = 'zinc' }) => (
  <div>
    <div
      className={`font-display text-2xl tabular-nums ${
        tone === 'amber' ? 'text-amber' : 'text-zinc-900'
      }`}
    >
      {n}
    </div>
    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
      {l}
    </div>
  </div>
);

// =================================================================
// DATA FLOW DIAGRAM (SVG)
// =================================================================
const DataFlow = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200">
    <SectionHeader
      kicker="The architecture · zero disruption"
      title={
        <>
          Data flows the way it always has —{' '}
          <span className="text-amber">we just modernise the middle.</span>
        </>
      }
    />
    <p className="text-zinc-700 max-w-3xl mt-4">
      TMG only processes data that SIAM already receives from its members.
      SIAM stays the data controller. No new collection. No OEM
      disintermediation.
    </p>

    <div className="mt-12 bg-zinc-50 border border-zinc-200 p-6 md:p-10 overflow-x-auto">
      <div className="md:hidden mb-3 inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.18em] text-amber border border-amber/30 px-2 py-1">
        Swipe →
      </div>
      <svg
        viewBox="0 0 1080 360"
        className="w-full"
        style={{ minWidth: 880 }}
      >
        <defs>
          <linearGradient id="gradAmber" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="gradBlue" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.6" />
          </linearGradient>
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="#F59E0B" />
          </marker>
        </defs>

        {/* Column 1 — OEMs */}
        <g>
          <text
            x="80"
            y="30"
            fill="#3B82F6"
            fontSize="11"
            fontFamily="monospace"
            letterSpacing="2"
          >
            500+ OEM &amp; COMPONENT MEMBERS
          </text>
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              <rect
                x="40"
                y={56 + i * 52}
                width="180"
                height="38"
                fill="#fafafa"
                stroke="rgba(59,130,246,0.5)"
              />
              <text
                x="56"
                y={80 + i * 52}
                fill="#18181b"
                fontSize="12"
                fontFamily="sans-serif"
              >
                {['Mahindra · CV', 'Tata Motors · PV', 'Hero MotoCorp · 2W', 'TVS · 2W/3W', '...498 more'][i]}
              </text>
            </g>
          ))}
        </g>

        {/* Arrow 1 */}
        <line
          x1="225"
          y1="180"
          x2="360"
          y2="180"
          stroke="url(#gradBlue)"
          strokeWidth="3"
          markerEnd="url(#arrow)"
        />
        <text
          x="290"
          y="170"
          textAnchor="middle"
          fill="#3B82F6"
          fontSize="10"
          fontFamily="monospace"
          letterSpacing="1.5"
        >
          monthly submission
        </text>

        {/* Column 2 — SIAM platform (with TMG inside) */}
        <g>
          <text
            x="380"
            y="30"
            fill="#F59E0B"
            fontSize="11"
            fontFamily="monospace"
            letterSpacing="2"
          >
            SIAM INTELLIGENCE PLATFORM · POWERED BY TMG
          </text>
          <rect
            x="370"
            y="56"
            width="320"
            height="248"
            fill="#f4f4f5"
            stroke="rgba(245,158,11,0.6)"
            strokeWidth="2"
          />
          {/* Inner layers */}
          {[
            { y: 70, label: 'Secure ingestion · CSV / API / portal' },
            { y: 110, label: 'Validation & normalisation engine' },
            { y: 150, label: 'Industry data lake · time-series ready' },
            { y: 190, label: 'Analytics & benchmarking engine' },
            { y: 230, label: 'Auto-report & narrative AI' },
            { y: 270, label: 'Member portal · APIs · dashboards' },
          ].map((row) => (
            <g key={row.y}>
              <rect
                x="380"
                y={row.y - 2}
                width="300"
                height="32"
                fill="#fafafa"
                stroke="rgba(0,0,0,0.08)"
              />
              <circle
                cx="395"
                cy={row.y + 14}
                r="3"
                fill="#F59E0B"
              />
              <text
                x="410"
                y={row.y + 18}
                fill="#3f3f46"
                fontSize="11"
                fontFamily="sans-serif"
              >
                {row.label}
              </text>
            </g>
          ))}
        </g>

        {/* Arrow 2 */}
        <line
          x1="695"
          y1="180"
          x2="830"
          y2="180"
          stroke="url(#gradAmber)"
          strokeWidth="3"
          markerEnd="url(#arrow)"
        />
        <text
          x="762"
          y="170"
          textAnchor="middle"
          fill="#F59E0B"
          fontSize="10"
          fontFamily="monospace"
          letterSpacing="1.5"
        >
          insights as a product
        </text>

        {/* Column 3 — Outputs */}
        <g>
          <text
            x="850"
            y="30"
            fill="#06B6D4"
            fontSize="11"
            fontFamily="monospace"
            letterSpacing="2"
          >
            MEMBERS · INDUSTRY · GOVT
          </text>
          {['Member CEO dashboards', 'Auto-generated reports', 'Government data feeds', 'Public press releases', 'Analyst & media APIs'].map((l, i) => (
            <g key={i}>
              <rect
                x="840"
                y={56 + i * 52}
                width="200"
                height="38"
                fill="#fafafa"
                stroke="rgba(6,182,212,0.5)"
              />
              <text
                x="856"
                y={80 + i * 52}
                fill="#18181b"
                fontSize="12"
                fontFamily="sans-serif"
              >
                {l}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>

    {/* Note */}
    <div className="mt-8 border-l-2 border-blue-500 bg-zinc-50 p-5 md:p-6 max-w-4xl">
      <div className="flex items-start gap-4">
        <ShieldCheck className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-display text-base text-zinc-900 mb-1">
            Privacy posture, stated upfront.
          </div>
          <p className="text-sm text-zinc-700 leading-relaxed">
            TMG only processes the data SIAM already receives from OEMs. SIAM
            remains the sole data controller. No data leaves the SIAM
            environment. No OEM-to-OEM identifying disclosures. DPDP-ready by
            design.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// =================================================================
// CAPABILITY STACK
// =================================================================
const STACK = [
  {
    icon: Database,
    n: '01',
    title: 'Secure ingestion',
    body:
      'Members submit via secure portal (drag-drop XLS / CSV), or push via authenticated API. Versioned, audited, signed.',
  },
  {
    icon: ShieldCheck,
    n: '02',
    title: 'Live validation',
    body:
      'Schema, ranges, growth-rate sanity checks, prior-period reconciliation. Issues flagged on submit — not 5 days later.',
  },
  {
    icon: Layers,
    n: '03',
    title: 'Industry data lake',
    body:
      'Cleansed, normalised, time-series-ready store. Decade-deep history, segment splits, geography, capacity, exports.',
  },
  {
    icon: LineChart,
    n: '04',
    title: 'Analytics & benchmarking',
    body:
      'Member-side dashboards: my share vs industry, my segment vs peers, my Tier-2 penetration vs Tier-1. All in their browser.',
  },
  {
    icon: Brain,
    n: '05',
    title: 'AI-generated reports',
    body:
      'Monthly, quarterly and event-driven narrative reports. Boardroom-ready PDF + interactive web. From 21 days to 2 minutes.',
  },
  {
    icon: Cpu,
    n: '06',
    title: 'APIs & integrations',
    body:
      'For analysts, media, govt and SIAM&apos;s own dashboards. Rate-limited, tiered, monetised. The data becomes a platform.',
  },
];

const CapabilityStack = () => (
  <section
    id="vision"
    className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200 bg-zinc-50"
  >
    <SectionHeader
      kicker="What TMG builds for SIAM"
      title={
        <>
          Six layers. <span className="text-amber">One platform.</span>
        </>
      }
    />

    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
      {STACK.map((s) => {
        const Icon = s.icon;
        return (
          <div key={s.n} className="bg-zinc-50 p-6 md:p-8">
            <div className="flex items-start justify-between mb-5">
              <div className="grid h-12 w-12 place-items-center border border-amber/40 bg-amber/10">
                <Icon className="w-5 h-5 text-amber" />
              </div>
              <span className="font-display text-2xl text-amber tabular-nums">
                {s.n}
              </span>
            </div>
            <div className="font-display text-xl text-zinc-900">{s.title}</div>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
              {s.body}
            </p>
          </div>
        );
      })}
    </div>
  </section>
);

// =================================================================
// VALUE TO SIAM
// =================================================================
const VALUE = [
  {
    big: '5× → 10×',
    label: 'Revenue path',
    body:
      "From ₹5 Cr a year of one-size reports to ₹25-50 Cr from member portal subscriptions, premium analytics, API tiers and bespoke benchmarking.",
  },
  {
    big: '~90%',
    label: 'Cycle compression',
    body:
      'From 21 days to under 24 hours between member submission and member-ready insight. Faster than any private data vendor.',
  },
  {
    big: 'Industry-wide',
    label: 'Member stickiness',
    body:
      'When member CEOs open SIAM dashboards weekly, SIAM moves from "the body that publishes reports" to "the platform we cannot operate without".',
  },
  {
    big: 'Authority+',
    label: 'Brand & policy weight',
    body:
      'Real-time SIAM dashboards become the citation for Bloomberg, BS, ET, PIB and the Ministry. Policy and lobbying weight compounds.',
  },
  {
    big: 'New surface',
    label: 'Adjacent revenue',
    body:
      'Analyst APIs, public-data feeds for media, advanced benchmarking for insurers/financiers, EV-readiness indices, export tracking products.',
  },
  {
    big: '0',
    label: 'Disruption to OEMs',
    body:
      "OEMs continue submitting to SIAM exactly as they do today — they just get a far better experience back. No new pipes for them to build.",
  },
];

const ValueToSIAM = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200">
    <SectionHeader
      kicker="What SIAM gets"
      title={
        <>
          A second business — built on top of the first,{' '}
          <span className="text-amber">without disturbing it.</span>
        </>
      }
    />
    <p className="text-zinc-700 max-w-3xl mt-4">
      The reports business stays. The member relationship stays. The OEM
      pipelines stay. Everything else compounds.
    </p>

    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
      {VALUE.map((v) => (
        <div key={v.label} className="bg-zinc-50 p-6 md:p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
            {v.label}
          </div>
          <div className="mt-2 font-display text-3xl md:text-4xl text-zinc-900 leading-tight">
            {v.big}
          </div>
          <p className="mt-4 text-sm text-zinc-600 leading-relaxed">{v.body}</p>
        </div>
      ))}
    </div>
  </section>
);

// =================================================================
// A FOUNDATION — NOT A CEILING
// =================================================================
const ProgressiveSIAM = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200 bg-zinc-50">
    <SectionHeader
      kicker="A foundation, not a ceiling"
      color="siam"
      title={
        <>
          We start with the data spine.{' '}
          <span style={{ color: '#3B82F6' }}>
            Then SIAM keeps moving up.
          </span>
        </>
      }
    />
    <p className="text-zinc-700 max-w-3xl mt-6 leading-relaxed">
      What we are proposing today is the simplest, highest-value first step
      for SIAM. Once SIAM&apos;s monthly data cycle runs on a modern
      platform, the very same foundation lets SIAM progressively become a
      fully digital, fully modern industry association — at SIAM&apos;s
      pace, in SIAM&apos;s control.
    </p>

    <div className="mt-12 grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
      {[
        {
          step: 'Today',
          when: 'Phase 1 · 0-6 months',
          title: 'The data spine',
          body:
            'Modernise the monthly cycle. Secure ingestion, live validation, member dashboards, AI-generated reports. The core SIAM cycle is digitised.',
          color: 'amber',
        },
        {
          step: 'Next',
          when: 'Phase 2 · 6-18 months',
          title: 'A digital SIAM',
          body:
            'A refreshed siam.in. Member subscriptions, event management and committee work-flows online. Press hub and policy briefs on the same platform.',
          color: 'siam',
        },
        {
          step: 'Onward',
          when: 'Phase 3 · 18 months+',
          title: 'A progressive association',
          body:
            'New data streams as SIAM chooses to add them — components, exports, EV batteries, R&D, lifecycle. Real-time feeds to MoRTH, PIB and global counterparts.',
          color: 'cyan',
        },
      ].map((p) => (
        <div key={p.step} className="bg-white p-6 md:p-8 relative">
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{
              background:
                p.color === 'siam'
                  ? '#3B82F6'
                  : p.color === 'amber'
                  ? '#F59E0B'
                  : '#06B6D4',
            }}
          />
          <div
            className="font-mono text-[10px] uppercase tracking-[0.22em]"
            style={{
              color:
                p.color === 'siam'
                  ? '#3B82F6'
                  : p.color === 'amber'
                  ? '#F59E0B'
                  : '#06B6D4',
            }}
          >
            {p.step} · {p.when}
          </div>
          <div className="font-display text-2xl text-zinc-900 mt-2">
            {p.title}
          </div>
          <p className="mt-4 text-sm text-zinc-700 leading-relaxed">{p.body}</p>
        </div>
      ))}
    </div>

    <div className="mt-10 border-l-2 border-amber bg-white p-5 md:p-6 max-w-4xl">
      <p className="text-sm md:text-base text-zinc-800 leading-relaxed">
        TMG can partner with SIAM far beyond data ingestion — from a refreshed
        digital identity to AI-led member services and public-facing
        intelligence products.{' '}
        <span className="text-amber font-medium">
          But the right way to start is to prove value on the data spine first.
        </span>{' '}
        Everything else then follows with confidence, sequencing and
        SIAM&apos;s full ownership.
      </p>
    </div>
  </section>
);

// =================================================================
// WHY TMG
// =================================================================
const WhyTMG = () => (
  <section
    id="why-tmg"
    className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200"
  >
    <SectionHeader
      kicker="Why TMG"
      title={
        <>
          Operators with platforms.{' '}
          <span className="text-amber">Not a consulting firm with a deck.</span>
        </>
      }
    />
    <div className="mt-12 grid md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
      {[
        {
          icon: Building2,
          t: 'Automotive operators',
          d:
            'Our team has run OEM functions, dealer networks, and built category-defining auto-tech businesses. We speak SIAM&apos;s language because it is our language.',
        },
        {
          icon: Layers,
          t: 'Platforms, not slideware',
          d:
            'MotoInsights.in · DLIP · Oto.bot · VehicleLedger.in · CarBikeClaims.com · BayWise.ai · AutoData.Exchange — production platforms you can build on today. We do not write a brief — we ship a working layer.',
        },
        {
          icon: ShieldCheck,
          t: 'Neutral & confidential',
          d:
            'TMG has no OEM equity at the SIAM-data layer. We are designed to be the neutral processor — what your members and your legal team will expect.',
        },
        {
          icon: Globe2,
          t: 'India-deep · globally read',
          d:
            "We operate at the level of MoRTH Vahan, FADA, SIAM and ACMA data — and benchmark to S&P, IHS, AlixPartners. We bring India-scale and global lens together.",
        },
      ].map((w) => {
        const Icon = w.icon;
        return (
          <div key={w.t} className="bg-zinc-50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 place-items-center border border-amber/40 bg-amber/10 flex-shrink-0">
                <Icon className="w-5 h-5 text-amber" />
              </div>
              <div>
                <div className="font-display text-xl text-zinc-900">{w.t}</div>
                <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
                  {w.d}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </section>
);

// =================================================================
// 90-DAY PLAN
// =================================================================
const PLAN = [
  {
    t: 'Days 0-30',
    title: 'Working session + pilot scope',
    bullets: [
      'Joint working sessions with SIAM data & member teams',
      'Audit current Excel pipelines + report templates',
      'Pick 1 segment (e.g. 2W) for a first live pilot',
      'Define SLAs, security model and member onboarding plan',
    ],
  },
  {
    t: 'Days 30-60',
    title: 'Pilot live · 1 segment, 1 report',
    bullets: [
      'Secure ingestion live for 20-30 willing member OEMs',
      'Validation engine + first live dashboard for that segment',
      'AI-generated monthly report drafted in <2 minutes',
      'Side-by-side comparison vs current cycle for SIAM team',
    ],
  },
  {
    t: 'Days 60-90',
    title: 'Roll-out plan + commercial model',
    bullets: [
      'Member rollout plan across segments (2W, 3W, PV, CV)',
      'Tiering: free for members, premium for advanced analytics',
      'API monetisation model for media, govt, analyst houses',
      'Year-2 product roadmap signed with SIAM leadership',
    ],
  },
];

const Plan = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200 bg-zinc-50">
    <SectionHeader
      kicker="From concept to live · 90 days"
      title={
        <>
          A respectful pilot.{' '}
          <span className="text-amber">A serious roadmap.</span>
        </>
      }
    />
    <div className="mt-12 grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
      {PLAN.map((p, i) => (
        <div key={p.t} className="bg-zinc-50 p-6 md:p-8 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
            {p.t}
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="font-display text-3xl text-amber tabular-nums">
              0{i + 1}
            </span>
            <span className="font-display text-xl text-zinc-900">{p.title}</span>
          </div>
          <ul className="mt-5 space-y-2.5">
            {p.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-zinc-700">
                <span className="w-1 h-1 rounded-full bg-amber mt-2 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

// =================================================================
// COMMERCIALS
// =================================================================
const Commercials = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200">
    <SectionHeader
      kicker="Engagement & commercials"
      title={
        <>
          How we partner.{' '}
          <span className="text-amber">Two honest options.</span>
        </>
      }
    />
    <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-5xl">
      {[
        {
          t: 'Build-Operate',
          tag: 'Conservative',
          body:
            'Fixed-fee build of the platform · ongoing operate retainer · SIAM owns IP and renames it as the SIAM Platform.',
          numbers: ['IP fully owned by SIAM', 'Predictable opex', 'Clear scope, clear milestones'],
        },
        {
          t: 'Revenue-share',
          tag: 'Aligned',
          body:
            'TMG builds and operates with minimal upfront. We share in incremental revenue generated by the new platform — skin-in-the-game from day one.',
          numbers: ['Low upfront for SIAM', 'TMG aligned to growth', 'Multi-year partnership'],
          highlight: true,
        },
      ].map((c) => (
        <div
          key={c.t}
          className={`bg-zinc-50 p-6 md:p-8 border ${
            c.highlight ? 'border-amber' : 'border-zinc-200'
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              {c.tag}
            </span>
            {c.highlight && (
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] bg-amber text-black px-2 py-0.5">
                Recommended
              </span>
            )}
          </div>
          <div className="font-display text-2xl text-zinc-900">{c.t}</div>
          <p className="mt-4 text-sm text-zinc-700 leading-relaxed">{c.body}</p>
          <ul className="mt-5 pt-5 border-t border-zinc-200 space-y-2">
            {c.numbers.map((n) => (
              <li
                key={n}
                className="flex items-start gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-600"
              >
                <Sparkles className="w-3 h-3 text-amber mt-0.5 flex-shrink-0" />
                {n}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>

    <p className="mt-10 text-sm text-zinc-600 max-w-3xl leading-relaxed">
      Commercials are open for discussion. We are happy to shape the model in
      a way that works best for SIAM&apos;s governance, member sensitivities
      and risk appetite.
    </p>
  </section>
);
// =================================================================
// CTA
// =================================================================
const FinalCTA = () => (
  <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-b border-zinc-200 relative overflow-hidden">
    <div
      className="absolute -bottom-32 right-1/4 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(circle, #F59E0B 0%, transparent 70%)' }}
    />
    <div className="relative max-w-5xl">
      <Kicker>One conversation away</Kicker>
      <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance text-zinc-900 leading-[1.05]">
        Bring TMG into a SIAM working group session.{' '}
        <span className="text-amber">Two hours. One whiteboard.</span>
      </h2>
      <p className="mt-6 text-zinc-700 text-base md:text-lg max-w-3xl">
        We&apos;ll walk through this in person — with the SIAM data team, a few
        member CEOs of your choosing, and a working sketch of the platform on
        the wall. By the end, you&apos;ll know if this is worth a 90-day pilot.
      </p>
      <div className="mt-10 flex flex-wrap gap-4">
        <Link
          to="/contact"
          data-testid="siam-cta-bring-us-in"
          className="inline-flex items-center gap-2 bg-amber text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:bg-amber/90"
        >
          Bring us into a session <ArrowUpRight className="w-4 h-4" />
        </Link>
        <a
          href="mailto:partner@themobilitygroup.co?subject=SIAM%20%C3%97%20TMG%20%E2%80%94%20Working%20session"
          className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-900 px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:border-amber hover:text-amber"
        >
          Email partner@themobilitygroup.co
        </a>
      </div>
    </div>
  </section>
);

// =================================================================
// FOOTER
// =================================================================
const Footer = () => (
  <footer className="px-6 md:px-12 lg:px-16 py-12 md:py-16">
    <div className="flex flex-wrap items-baseline justify-between gap-4">
      <div className="flex items-center gap-3">
        <SIAMWordmark small />
        <span className="font-display text-base text-zinc-500">×</span>
        <span className="grid h-8 w-8 place-items-center border border-amber bg-amber/10">
          <span className="font-mono text-amber text-xs font-semibold">TMG</span>
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 ml-3">
          Concept presentation · for SIAM leadership
        </span>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
        themobilitygroup.co / projects / SIAM
      </div>
    </div>
    <p className="mt-8 text-[11px] text-zinc-500 leading-relaxed max-w-4xl">
      All figures and architecture are directional, illustrative and prepared
      for a closed discussion with SIAM leadership. SIAM marks shown for
      context only — The Mobility Group is not affiliated with or endorsed by
      the Society of Indian Automobile Manufacturers. Privacy posture: TMG
      proposes to process only data that SIAM already receives from its
      members. SIAM remains the sole data controller throughout.
    </p>
  </footer>
);

// =================================================================
// PAGE
// =================================================================
const SIAMProject = () => {
  useSIAMMeta();
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  });

  if (!unlocked) return <Gate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-40 border-b border-zinc-200 bg-white/80 backdrop-blur-xl">
        <div className="px-6 md:px-12 lg:px-16 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-testid="siam-back"
            className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:text-amber"
          >
            <SIAMWordmark small />
            <span className="text-zinc-600">×</span>
            <span className="text-amber">TMG</span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="/api/projects/siam/pdf"
              target="_blank"
              rel="noopener"
              data-testid="siam-top-pdf"
              className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] hover:border-amber hover:text-amber"
            >
              <Download className="w-3 h-3" /> PDF
            </a>
            <Link
              to="/contact"
              data-testid="siam-top-contact"
              className="inline-flex items-center gap-2 bg-amber text-black px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-amber/90"
            >
              Bring us in
            </Link>
          </div>
        </div>
      </div>

      <Hero />
      <StatusQuo />
      <BeforeAfter />
      <DataFlow />
      <CapabilityStack />
      <ValueToSIAM />
      <ProgressiveSIAM />
      <WhyTMG />
      <Plan />
      <Commercials />
      <FinalCTA />
      <Footer />
    </div>
  );
};

export default SIAMProject;
